import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTeacher } from '@/context/TeacherContext';
import { useUserProfile } from '@/context/UserProfileContext';
import { useSubjectChapter } from '@/context/SubjectChapterContext';
import { useDiscovery } from '@/context/DiscoveryContext';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ConfirmationModal } from '@/components/ui/ConfirmationModal';
import { cn } from '@/lib/utils';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Grid3X3,
    ChevronDown,
    Menu,
    User,
    Settings,
    LogOut,
    HelpCircle,
    Sparkles,
    BookOpen,
    AlertCircle,
    X,
    Lock,
    Bell
} from 'lucide-react';
import { useNotifications } from '@/hooks/useNotifications';
import NotificationList from '@/components/Notifications/NotificationList';
import { motion, AnimatePresence } from 'framer-motion';
import { clearAuthTokens, clearAllSessions, getUserData, performLogoutTeardown } from '@/utils/auth';
import { formatBoardLabel } from '@/utils/boardUtils';

// --- Main Header Component ---
export default function TeacherHeader({ onOpenSidebar }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { teacherTheme, toggleTheme, user } = useTeacher();
    const { profile } = useUserProfile();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const { unreadCount, refreshUnreadCount } = useNotifications(false);
    const [isNotifOpen, setIsNotifOpen] = useState(false);
    const isDark = teacherTheme === 'dark';

    const userData = getUserData('teacher') || {};
    const isTutor = profile?.is_tutor_configured ?? userData.is_tutor_configured ?? false;

    // Subject, Grade, Syllabus & Chapter selection for Teach page
    const {
        selectedSubject,
        selectedChapter,
        selectedSubjectId,
        selectedChapterId,
        selectedBoardId,
        selectedGradeId,
        updateSubject,
        updateChapter,
        updateBoard,
        updateGrade,
        subjects = [],
        chapters = [],
        boards = [],
        grades = [],
        loadingSubjects,
        loadingChapters,
        loadingBoards,
        loadingGrades,
        hasNoAssignedSubjects,
        lockedCurriculum,
    } = useSubjectChapter();

    const displayBoards = boards || [];
    const displayGrades = grades || [];

    const pathname = location.pathname || '';
    const isTeachPage = pathname === '/teacher' || pathname === '/teacher/' || pathname === '/teacher/teach' || pathname.startsWith('/teacher/teach/');
    const [isSelectionExpanded, setIsSelectionExpanded] = useState(false);

    // Ensure selection modal closes if navigating away from Teach page
    useEffect(() => {
        if (!isTeachPage && isSelectionExpanded) {
            setIsSelectionExpanded(false);
        }
    }, [isTeachPage, isSelectionExpanded]);

    const canClose = hasNoAssignedSubjects || !!(selectedSubjectId && selectedChapterId) || (!loadingSubjects && subjects?.length === 0);

    // Dynamic selector label
    const selectorLabel = useMemo(() => {
        if (hasNoAssignedSubjects) return 'No Subjects Assigned';
        if (selectedChapter) return selectedChapter;
        if (selectedChapterId && chapters?.length > 0) {
            const chap = chapters.find(c => String(c.id) === String(selectedChapterId));
            if (chap?.label) return chap.label;
        }
        if (selectedSubject) return selectedSubject;
        if (selectedSubjectId && subjects?.length > 0) {
            const sub = subjects.find(s => String(s.id) === String(selectedSubjectId));
            if (sub?.label) return sub.label;
        }
        return 'Select Focus';
    }, [hasNoAssignedSubjects, selectedChapter, selectedSubject, selectedChapterId, selectedSubjectId, chapters, subjects]);

    // Body scroll lock when selection modal is open
    useEffect(() => {
        if (isSelectionExpanded) {
            const originalStyle = window.getComputedStyle(document.body).overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalStyle;
            };
        }
    }, [isSelectionExpanded]);

    // Close on escape key
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape' && canClose) setIsSelectionExpanded(false);
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [canClose]);

    const handleLogoutConfirmed = async () => {
        try {
            clearAuthTokens('teacher');
        } catch (error) {
            console.error('Error during logout:', error);
        } finally {
            setShowLogoutConfirm(false);
            await performLogoutTeardown('/login');
        }
    };

    return (
        <header className={cn(
            "w-full pt-safe-top lg:pt-0 border-b z-30 transition-colors shrink-0",
            "bg-[hsl(var(--card))] border-[hsl(var(--border))]"
        )}>
            <div className="w-full h-16 px-3 sm:px-6 pl-[calc(0.75rem+max(env(safe-area-inset-left,0px),var(--safe-area-inset-left,0px)))] pr-[calc(0.75rem+max(env(safe-area-inset-right,0px),var(--safe-area-inset-right,0px)))] lg:pl-6 lg:pr-6 flex items-center justify-between">
                {/* Left Side: Menu + Brand for mobile */}
                <div className="flex items-center gap-2 sm:gap-3 min-w-0 shrink">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="w-9 h-9 rounded-lg lg:hidden shrink-0 hover:bg-accent"
                        onClick={onOpenSidebar}
                        aria-label="Open sidebar"
                    >
                        <Menu className="w-5 h-5 text-muted-foreground" />
                    </Button>

                    {/* Brand visible in mobile layout when sidebar is collapsed */}
                    <div 
                        className="flex lg:hidden flex-col justify-center cursor-pointer hover:opacity-90 transition-opacity min-w-0"
                        onClick={() => navigate('/teacher/teach')}
                    >
                        <img
                            src="/logo_tagline_black.png"
                            alt="Budlee AI"
                            className="dark:hidden h-8 sm:h-8.5 object-contain w-auto max-w-[170px] sm:max-w-[190px]"
                            onContextMenu={(e) => e.preventDefault()}
                        />
                        <img
                            src="/logo_tagline_white.png"
                            alt="Budlee AI"
                            className="hidden dark:block h-8 sm:h-8.5 object-contain w-auto max-w-[170px] sm:max-w-[190px]"
                            onContextMenu={(e) => e.preventDefault()}
                        />
                    </div>
                </div>

                {/* Right Side: Syllabus/Grade/Subject/Chapter Selector (Mobile on Teach page) & Desktop Global Controls */}
                <div className="flex items-center gap-2 sm:gap-3">
                {isTeachPage && (
                    <div className="flex lg:hidden items-center gap-1.5 min-w-0">
                        <div className="flex items-center bg-[hsl(var(--primary))]/30 hover:bg-[hsl(var(--primary))]/40 rounded-full border border-[hsl(var(--primary))]/60 h-8 shadow-sm font-bold min-w-0 max-w-[145px] xs:max-w-[175px] sm:max-w-[220px] overflow-hidden transition-colors">
                            <button
                                type="button"
                                onClick={() => setIsSelectionExpanded(true)}
                                className="flex items-center gap-1 pl-2.5 pr-2 h-full hover:bg-black/5 dark:hover:bg-white/5 active:bg-black/10 transition-colors min-w-0 flex-1 text-left"
                            >
                                <span className="truncate text-[10px] font-black uppercase tracking-wider text-foreground">
                                    {selectorLabel}
                                </span>
                                <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-100 text-foreground" />
                            </button>
                        </div>
                    </div>
                )}

                {/* Notification Bell (Visible on both Mobile & Desktop) */}
                <div className="relative">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="relative w-9 h-9 rounded-xl hover:bg-accent text-foreground"
                        onClick={() => setIsNotifOpen(!isNotifOpen)}
                        aria-label="Notifications"
                        title="Notifications"
                    >
                        <Bell className="w-5 h-5 text-muted-foreground" />
                        {unreadCount > 0 && (
                            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full text-[10px] font-black text-white flex items-center justify-center border-2 border-[hsl(var(--card))] animate-bounce">
                                {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                        )}
                    </Button>

                    <AnimatePresence>
                        {isNotifOpen && (
                            <>
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="fixed inset-0 z-[150] bg-black/20 backdrop-blur-xs"
                                    onClick={() => setIsNotifOpen(false)}
                                />
                                <motion.div
                                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                    className="absolute right-0 top-12 z-[160] w-[340px] max-w-[calc(100vw-1.5rem)]"
                                >
                                    <NotificationList
                                        onClose={() => setIsNotifOpen(false)}
                                        onRefreshCount={refreshUnreadCount}
                                    />
                                </motion.div>
                            </>
                        )}
                    </AnimatePresence>
                </div>

                {/* Desktop Global Controls (Hidden in mobile layout) */}
                <div className="hidden lg:flex items-center gap-3">
                    {/* Theme Toggle */}
                    <ThemeToggle isDark={isDark} onToggle={toggleTheme} />

                    {/* Profile Dropdown */}
                    <div className="relative">
                        <button
                            onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                            className="flex items-center gap-3 ml-2 pl-2 border-l border-border hover:opacity-80 transition-opacity"
                        >
                            <div className="text-right hidden md:block">
                                <div className="text-sm font-medium text-foreground">
                                    {user.name}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    {user.role}
                                </div>
                            </div>
                            <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden border border-border bg-[hsl(var(--gold-primary))]/15 text-[hsl(var(--gold-primary))]">
                                {user.avatar ? (
                                    <img 
                                        src={user.avatar} 
                                        alt={user.name} 
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <User className="w-5 h-5" />
                                )}
                            </div>
                        </button>

                        {isProfileDropdownOpen && (
                            <>
                                <div
                                    className="fixed inset-0 z-40"
                                    onClick={() => setIsProfileDropdownOpen(false)}
                                ></div>
                                <div className="absolute top-full right-0 mt-2 w-48 rounded-xl shadow-lg z-50 bg-card border border-border overflow-hidden">
                                    <div className="p-2 space-y-1">
                                        <button
                                            onClick={() => {
                                                navigate('/teacher/profile');
                                                setIsProfileDropdownOpen(false);
                                            }}
                                            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors hover:bg-accent text-foreground">
                                            <User className="w-4 h-4" />
                                            <span>My Profile</span>
                                        </button>

                                        <div className="my-1 border-t border-border" />
                                        <button
                                            onClick={() => {
                                                setIsProfileDropdownOpen(false);
                                                setShowLogoutConfirm(true);
                                            }}
                                            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors bg-red-500 hover:bg-red-600 text-white"
                                        >
                                            <LogOut className="w-4 h-4" />
                                            <span>Logout</span>
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
            </div>

            {/* Syllabus, Grade, Subject, Chapter Selection Modal Overlay (Mobile) */}
            {isTeachPage && isSelectionExpanded && typeof document !== 'undefined' && createPortal(
                <div
                    className="fixed inset-0 z-[15500] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 lg:hidden"
                    onClick={() => canClose && setIsSelectionExpanded(false)}
                >
                    <div className="w-full max-w-sm" onClick={e => e.stopPropagation()}>
                        <Card className="bg-gradient-to-br from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] border-0 shadow-2xl relative rounded-3xl">
                            {/* Decorative background icons */}
                            <Sparkles className="absolute -top-4 -right-4 w-24 h-24 text-white/10 rotate-12 pointer-events-none" />
                            <BookOpen className="absolute -bottom-4 -left-4 w-20 h-20 text-white/5 -rotate-12 pointer-events-none" />

                            <CardContent className="p-4 sm:p-5 space-y-4">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-sm font-bold text-white">
                                        Select Teaching Focus
                                    </h2>
                                    {canClose && (
                                        <button
                                            onClick={() => setIsSelectionExpanded(false)}
                                            className="p-1 rounded-full bg-white/10 text-white/80 hover:text-white active:scale-95 transition-all"
                                        >
                                            <X className="w-5 h-5" />
                                        </button>
                                    )}
                                </div>

                                {hasNoAssignedSubjects ? (
                                    <div className="py-6 px-4 rounded-2xl bg-black/20 text-center space-y-3">
                                        <div className="w-12 h-12 mx-auto rounded-full bg-white/10 flex items-center justify-center text-white">
                                            <BookOpen className="w-6 h-6" />
                                        </div>
                                        <h3 className="text-sm font-bold text-white">No Subjects Assigned</h3>
                                        <p className="text-xs text-white/80 leading-relaxed">
                                            No subjects are currently assigned to your tutor profile. Please contact your administrator to configure your teaching subjects.
                                        </p>
                                        <Button
                                            onClick={() => setIsSelectionExpanded(false)}
                                            className="w-full h-10 bg-white/20 hover:bg-white/30 text-white font-bold text-xs rounded-xl mt-2"
                                        >
                                            Close
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {/* Live Class Curriculum Restriction Banner */}
                                        {lockedCurriculum?.isLocked && (
                                            <div className="flex items-center gap-2 p-2.5 rounded-xl bg-amber-500/20 border border-amber-500/30 text-white text-[11px] font-medium leading-tight">
                                                <Lock className="w-3.5 h-3.5 shrink-0 text-amber-300" />
                                                <span>Live Class active: Syllabus & Grade locked to student's profile ({lockedCurriculum.boardName || 'Syllabus'}, {lockedCurriculum.gradeName || 'Grade'}).</span>
                                            </div>
                                        )}

                                        {/* 1. Syllabus / Board */}
                                        <div className="space-y-1.5 overflow-visible">
                                            <div className="flex items-center justify-between ml-1">
                                                <label className="text-[10px] uppercase font-black text-white/70 tracking-wider flex items-center gap-1">
                                                    {lockedCurriculum?.isLocked && <Lock className="w-2.5 h-2.5 text-amber-300 inline" />}
                                                    <span>Syllabus / Board</span>
                                                </label>
                                                {lockedCurriculum?.isLocked && (
                                                    <span className="text-[9px] uppercase font-bold text-amber-300 tracking-wider">Locked</span>
                                                )}
                                            </div>
                                            <Select
                                                value={selectedBoardId ? selectedBoardId.toString() : ''}
                                                onValueChange={(val) => {
                                                    const found = displayBoards.find(b => String(b.id) === val);
                                                    updateBoard(val, found ? (found.board_code || found.label || found.board_name || found.name) : '');
                                                }}
                                                disabled={loadingBoards && displayBoards.length === 0}
                                            >
                                                <SelectTrigger className="w-full h-10 bg-white dark:bg-black/20 border-0 dark:border dark:border-white/10 text-black dark:text-white rounded-xl font-bold shadow-sm focus:ring-0 focus:ring-offset-0 text-xs">
                                                    <SelectValue placeholder={loadingBoards && displayBoards.length === 0 ? "Loading..." : "Select Syllabus"} />
                                                </SelectTrigger>
                                                <SelectContent className="z-[20000] bg-white dark:bg-[hsl(var(--card-bg))] border border-gray-100 dark:border-white/10 rounded-xl shadow-xl max-h-[220px] overflow-y-auto">
                                                    {displayBoards.map(b => {
                                                        const isBoardDisabled = Boolean(lockedCurriculum?.isLocked && lockedCurriculum.boardId && String(b.id) !== String(lockedCurriculum.boardId));
                                                        return (
                                                            <SelectItem
                                                                key={b.id}
                                                                value={b.id.toString()}
                                                                disabled={isBoardDisabled}
                                                                className={cn(
                                                                    "font-semibold py-2.5 border-b border-gray-50 dark:border-white/5 last:border-0 text-xs flex items-center justify-between",
                                                                    isBoardDisabled
                                                                        ? "opacity-35 cursor-not-allowed bg-black/5 dark:bg-white/5 line-through text-muted-foreground"
                                                                        : "hover:bg-gray-50 dark:hover:bg-white/5 text-black dark:text-white"
                                                                )}
                                                            >
                                                                <div className="flex items-center justify-between w-full gap-2">
                                                                    <span>{formatBoardLabel(b)}</span>
                                                                    {isBoardDisabled && (
                                                                        <span className="text-[9px] uppercase font-bold text-amber-500 shrink-0">(Locked)</span>
                                                                    )}
                                                                </div>
                                                            </SelectItem>
                                                        );
                                                    })}
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        {/* 2. Grade */}
                                        <div className="space-y-1.5 overflow-visible">
                                            <div className="flex items-center justify-between ml-1">
                                                <label className="text-[10px] uppercase font-black text-white/70 tracking-wider flex items-center gap-1">
                                                    {lockedCurriculum?.isLocked && <Lock className="w-2.5 h-2.5 text-amber-300 inline" />}
                                                    <span>Grade</span>
                                                </label>
                                                {lockedCurriculum?.isLocked && (
                                                    <span className="text-[9px] uppercase font-bold text-amber-300 tracking-wider">Locked</span>
                                                )}
                                            </div>
                                            <Select
                                                value={selectedGradeId ? selectedGradeId.toString() : ''}
                                                onValueChange={(val) => {
                                                    const found = displayGrades.find(g => String(g.id) === val);
                                                    updateGrade(val, found ? (found.label || found.grade_name || found.name) : '');
                                                }}
                                                disabled={!selectedBoardId || (loadingGrades && displayGrades.length === 0)}
                                            >
                                                <SelectTrigger className="w-full h-10 bg-white dark:bg-black/20 border-0 dark:border dark:border-white/10 text-black dark:text-white rounded-xl font-bold shadow-sm focus:ring-0 focus:ring-offset-0 text-xs">
                                                    <SelectValue placeholder={loadingGrades && displayGrades.length === 0 ? "Loading..." : "Select Grade"} />
                                                </SelectTrigger>
                                                <SelectContent className="z-[20000] bg-white dark:bg-[hsl(var(--card-bg))] border border-gray-100 dark:border-white/10 rounded-xl shadow-xl max-h-[220px] overflow-y-auto">
                                                    {displayGrades.map(g => {
                                                        const isGradeDisabled = Boolean(lockedCurriculum?.isLocked && lockedCurriculum.gradeId && String(g.id) !== String(lockedCurriculum.gradeId));
                                                        return (
                                                            <SelectItem
                                                                key={g.id}
                                                                value={g.id.toString()}
                                                                disabled={isGradeDisabled}
                                                                className={cn(
                                                                    "font-semibold py-2.5 border-b border-gray-50 dark:border-white/5 last:border-0 text-xs flex items-center justify-between",
                                                                    isGradeDisabled
                                                                        ? "opacity-35 cursor-not-allowed bg-black/5 dark:bg-white/5 line-through text-muted-foreground"
                                                                        : "hover:bg-gray-50 dark:hover:bg-white/5 text-black dark:text-white"
                                                                )}
                                                            >
                                                                <div className="flex items-center justify-between w-full gap-2">
                                                                    <span>{g.label || g.grade_name || g.name}</span>
                                                                    {isGradeDisabled && (
                                                                        <span className="text-[9px] uppercase font-bold text-amber-500 shrink-0">(Locked)</span>
                                                                    )}
                                                                </div>
                                                            </SelectItem>
                                                        );
                                                    })}
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        {/* 3. Subject */}
                                        <div className="space-y-1.5 overflow-visible">
                                            <label className="text-[10px] uppercase font-black text-white/70 ml-1 tracking-wider">Subject</label>
                                            <Select
                                                value={selectedSubjectId ? selectedSubjectId.toString() : ''}
                                                onValueChange={(value) => {
                                                    const subject = subjects.find(s => s.id.toString() === value);
                                                    if (subject) {
                                                        updateSubject(subject.id, subject.label || subject.subject_name || subject.name);
                                                        updateChapter(null, '');
                                                    }
                                                }}
                                                disabled={loadingSubjects || !selectedGradeId}
                                            >
                                                <SelectTrigger className="w-full h-10 bg-white dark:bg-black/20 border-0 dark:border dark:border-white/10 text-black dark:text-white rounded-xl font-bold shadow-sm focus:ring-0 focus:ring-offset-0 text-xs">
                                                    <SelectValue placeholder={loadingSubjects ? "Loading..." : "Select Subject"} />
                                                </SelectTrigger>
                                                <SelectContent className="z-[20000] bg-white dark:bg-[hsl(var(--card-bg))] border border-gray-100 dark:border-white/10 rounded-xl shadow-xl max-h-[220px] overflow-y-auto">
                                                    {subjects.map(subject => (
                                                        <SelectItem key={subject.id} value={subject.id.toString()} className="font-semibold py-2.5 border-b border-gray-50 dark:border-white/5 last:border-0 hover:bg-gray-50 dark:hover:bg-white/5 text-xs text-black dark:text-white">
                                                            {subject.label || subject.subject_name || subject.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        {/* 4. Chapter */}
                                        <div className="space-y-1.5 overflow-visible">
                                            <label className="text-[10px] uppercase font-black text-white/70 ml-1 tracking-wider">Chapter</label>
                                            <Select
                                                value={selectedChapterId ? selectedChapterId.toString() : ''}
                                                onValueChange={(value) => {
                                                    const chapter = chapters.find(c => c.id.toString() === value);
                                                    if (chapter) {
                                                        updateChapter(chapter.id, chapter.label || chapter.chapter_name || chapter.name);
                                                    }
                                                }}
                                                disabled={!selectedSubjectId || loadingChapters}
                                            >
                                                <SelectTrigger className="w-full h-10 bg-white dark:bg-black/20 border-0 dark:border dark:border-white/10 text-black dark:text-white rounded-xl font-bold disabled:opacity-50 shadow-sm focus:ring-0 focus:ring-offset-0 text-xs">
                                                    <SelectValue placeholder={loadingChapters ? "Loading..." : "Select Chapter"} />
                                                </SelectTrigger>
                                                <SelectContent className="z-[20000] bg-white dark:bg-[hsl(var(--card-bg))] border border-gray-100 dark:border-white/10 rounded-xl shadow-xl max-h-[220px] overflow-y-auto">
                                                    {chapters.map(chapter => (
                                                        <SelectItem key={chapter.id} value={chapter.id.toString()} className="font-semibold py-2.5 border-b border-gray-50 dark:border-white/5 last:border-0 hover:bg-gray-50 dark:hover:bg-white/5 text-xs text-black dark:text-white">
                                                            {chapter.label || chapter.chapter_name || chapter.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        <Button
                                            onClick={() => canClose && setIsSelectionExpanded(false)}
                                            disabled={!canClose}
                                            className="w-full h-11 bg-[hsl(var(--main-bg))] hover:bg-[hsl(var(--main-bg))]/90 text-[hsl(var(--foreground))] font-black text-sm rounded-xl mt-2 shadow-lg active:scale-[0.98] transition-all disabled:opacity-50"
                                        >
                                            Done
                                        </Button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>,
                document.body
            )}

            <ConfirmationModal
                isOpen={showLogoutConfirm}
                onOpenChange={setShowLogoutConfirm}
                title="Log out"
                description="You will be signed out of the teacher portal and returned to login."
                confirmText="Log out"
                cancelText="Stay logged in"
                onConfirm={handleLogoutConfirmed}
                onCancel={() => setShowLogoutConfirm(false)}
            />
        </header>
    );
}
